import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Users, CheckCircle, HandshakeIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const CoreValuesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[350px] flex items-center justify-center overflow-hidden bg-[#0077B6]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077B6] to-[#005A8C]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-white">Our Core Values</h1>
          <p className="text-lg text-blue-100">
            The principles that guide everything we do
          </p>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-8"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              At Utmost Financial, our core values shape our culture and define how we serve our clients. These principles are at the heart of every interaction and decision we make.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <Card className="bg-white border border-gray-200 hover:border-[#0077B6] transition-all border-l-4 border-l-[#0077B6] hover:shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-6">
                  <Shield className="h-8 w-8 text-[#0077B6]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Integrity</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We believe in doing the right thing, always. Our commitment to honesty and transparency means you can trust that we'll provide unbiased advice that's truly in your best interest.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Honest and transparent communication</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Unbiased financial recommendations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Ethical business practices</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border border-gray-200 hover:border-[#0077B6] transition-all border-l-4 border-l-[#D4AF37] hover:shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-xl bg-[#D4AF37]/10 border-2 border-[#D4AF37] flex items-center justify-center mb-6">
                  <Users className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Client Focus</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Your goals are our priority. We take the time to understand your unique financial situation and create personalized solutions that align with your aspirations and values.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Personalized service and attention</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Tailored financial strategies</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Responsive and accessible advisors</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border border-gray-200 hover:border-[#0077B6] transition-all border-l-4 border-l-[#0077B6] hover:shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-6">
                  <CheckCircle className="h-8 w-8 text-[#0077B6]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Excellence</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We're committed to delivering the highest quality service and expertise. Our team continuously learns and adapts to provide you with the best financial solutions available.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Continuous professional development</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Industry-leading knowledge</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Commitment to quality outcomes</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border border-gray-200 hover:border-[#0077B6] transition-all border-l-4 border-l-[#D4AF37] hover:shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-xl bg-[#D4AF37]/10 border-2 border-[#D4AF37] flex items-center justify-center mb-6">
                  <HandshakeIcon className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Partnership</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We view our relationship with clients as a partnership. We're here for the long term, supporting you through every stage of your financial journey and celebrating your successes.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Long-term relationship building</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Collaborative approach to planning</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Ongoing support and guidance</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Experience Our Values in Action</h2>
          <p className="text-blue-100 mb-6 text-lg">
            Connect with our team to see how our values translate into exceptional service
          </p>
          <Button asChild size="lg" className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold text-lg px-8">
            <Link to="/team">Meet Our Team</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default CoreValuesPage;
